import {
  Banner,
  CardExpand,
  CardTitleWithIcon,
  Heading,
  LoadingSpinnerCenter,
  Subtitle,
} from '@trevor/ui-components';
import React from 'react';
import {
  useChartConfiguration,
  useFetchCreditsData,
} from '@/features/credits/hooks';
import { SUBSCRIPTION, TMP_formatNumber } from '@/features/credits/utils';
import { Doughnut } from 'react-chartjs-2';
import { Activity } from '@/features/credits/components/HowYouUseCredits/components/Activity/Activity';
import {
  CardContent,
  ChartContainer,
  ColumnGap32Container,
  CreditCounter,
  LearnMore,
} from '@/features/credits/components/HowYouUseCredits/HowYouUseCredits.styles';
import { TSubscription } from '@/features/credits/types';
import { useToggle } from '@/hooks';
import { getDateInterval } from '@/features/credits/components/utils';

const INFO: Record<TSubscription, string> = {
  // eslint-disable-next-line max-len
  [SUBSCRIPTION.free]:
    "This shows the number of credits you’ve used in the last 30 days. Below, you can see a breakdown of how you’ve used those credits. If you exceed 3000 credits in a 30 day period, you'll need to upgrade to a paid plan to continue using Trevor.",
  [SUBSCRIPTION.pro]:
    'This shows the number of credits you’ve used in the last 30 days. Below, you can see a breakdown of how you’ve used those credits.',
  [SUBSCRIPTION.enterprise]:
    'This shows the number of credits you’ve used in the last 30 days. Below, you can see a breakdown of how you’ve used those credits.',
};

export const HowYouUseCredits = () => {
  const { subscriptionDetails, creditsConsumptionInfo, isLoading } =
    useFetchCreditsData();
  const {
    data: chartData,
    options,
    totalConsumedCredits,
  } = useChartConfiguration(subscriptionDetails, creditsConsumptionInfo);
  const [expanded, toggleExpanded] = useToggle(false, true);

  const isFetching =
    isLoading || !(subscriptionDetails && creditsConsumptionInfo && chartData);

  const isFreePlan = subscriptionDetails?.name === SUBSCRIPTION.free;
  const fromDate = new Date();
  fromDate.setDate(fromDate.getDate() - 30);

  return (
    <CardExpand
      expanded={isFreePlan || expanded}
      onExpand={toggleExpanded}
      hideExpandButton={isFreePlan || isFetching}
      width="100%"
      minHeight="176px"
      padding={48}
      gap={16}
      header={
        !isFetching ? (
          <CardTitleWithIcon
            title="How you use credits"
            iconName="illustration-usage"
          />
        ) : null
      }
    >
      {isFetching ? (
        <LoadingSpinnerCenter size="lg" />
      ) : (
        <CardContent>
          <ColumnGap32Container>
            <Subtitle variant={2}>
              {getDateInterval(fromDate, new Date())}
            </Subtitle>

            <ChartContainer>
              <Doughnut data={chartData} options={options} />
              <CreditCounter>
                <Heading variant={3}>
                  {TMP_formatNumber(totalConsumedCredits ?? 0)}/
                  {TMP_formatNumber(subscriptionDetails.totalCredits)}
                </Heading>
                <Subtitle variant={1}>Credits</Subtitle>
              </CreditCounter>
            </ChartContainer>
            <Banner severity="info">
              {INFO[subscriptionDetails.name as TSubscription]}
              <LearnMore href="https://trevor.io/pricing/" target="_blank">
                Learn more about credits
              </LearnMore>
            </Banner>
          </ColumnGap32Container>
          <ColumnGap32Container>
            <Heading variant={3}>Your usage breakdown</Heading>
            {creditsConsumptionInfo.consumption
              .flatMap((consumption) => consumption.children ?? consumption)
              .map(({ categoryName, type, consumedCredits }) => (
                <Activity
                  key={`${categoryName}:${type}`}
                  type={type}
                  totalCredits={creditsConsumptionInfo.totalConsumedCredits}
                  category={categoryName}
                  consumption={consumedCredits}
                />
              ))}
          </ColumnGap32Container>
        </CardContent>
      )}
    </CardExpand>
  );
};
